import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
    Phone,
    Mail,
    MessageCircle,
    Globe,
    Linkedin,
    Instagram,
    Twitter,
    Facebook,
    Loader2,
    Building2,
    Briefcase,
} from "lucide-react";
import Footer from "../components/Footer";

const API = process.env.REACT_APP_BACKEND_URL || "";

function digitsOnly(s) {
    return String(s || "").replace(/\D/g, "");
}

export default function VirtualCard() {
    const { identifier } = useParams();
    const [card, setCard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/api/card/${encodeURIComponent(identifier)}`);
                setCard(data);
            } catch (e) {
                setErr(e.response?.status === 404 ? "Tarjeta no encontrada" : "No se pudo cargar la tarjeta");
            } finally {
                setLoading(false);
            }
        })();
    }, [identifier]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <Loader2 className="w-5 h-5 animate-spin text-orange-500" />
            </div>
        );
    }
    if (err || !card) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 text-center px-6">
                <img src="/logo-emprenovus.png" alt="Emprenovus" className="h-12 mb-6" />
                <h1 className="text-xl font-display font-semibold text-slate-900 mb-2">{err || "Tarjeta no encontrada"}</h1>
                <p className="text-sm text-slate-500">Verifica el enlace o solicita uno nuevo al propietario.</p>
            </div>
        );
    }

    const whatsappDigits = digitsOnly(card.whatsapp || card.phone);
    const initials = (card.name || "?").trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("");

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex flex-col">
            <div className="flex-1 px-4 py-8 flex items-start justify-center">
                <div className="w-full max-w-md" data-testid="virtual-card">
                    {/* Header / banner — relative so the avatar (absolute) anchors to
                        its bottom edge. NO overflow-hidden so the avatar can extend
                        below the banner without being clipped. */}
                    <div className="bg-slate-900 rounded-t-2xl px-6 pt-10 pb-14 sm:pb-16 text-center relative">
                        <img src="/logo-emprenovus.png" alt="Emprenovus" className="h-7 mx-auto opacity-80" />
                        <div className="absolute left-1/2 -translate-x-1/2 -bottom-14 sm:-bottom-16 w-28 h-28 sm:w-32 sm:h-32 rounded-full bg-white p-1 shadow-lg shadow-black/20 z-10">
                            {card.photo_url ? (
                                <img src={card.photo_url} alt={card.name} className="w-full h-full rounded-full object-cover" data-testid="virtual-card-photo" />
                            ) : (
                                <div className="w-full h-full rounded-full bg-orange-500 text-white flex items-center justify-center text-2xl font-display font-bold">
                                    {initials || "?"}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Body */}
                    <div className="bg-white rounded-b-2xl px-6 pt-20 sm:pt-24 pb-6 shadow-xl shadow-black/5 border border-slate-200 border-t-0">
                        <h1 className="text-2xl font-display font-semibold text-center text-slate-900" data-testid="virtual-card-name">{card.name}</h1>
                        {card.position && (
                            <div className="text-sm text-orange-700 text-center flex items-center justify-center gap-1 mt-1">
                                <Briefcase className="w-3.5 h-3.5" /> {card.position}
                            </div>
                        )}
                        {card.company && (
                            <div className="text-sm text-slate-600 text-center flex items-center justify-center gap-1 mt-0.5">
                                <Building2 className="w-3.5 h-3.5" /> {card.company}
                            </div>
                        )}
                        {card.bio && (
                            <p className="text-sm text-slate-600 text-center mt-3 whitespace-pre-line">{card.bio}</p>
                        )}

                        {/* Primary action buttons */}
                        <div className="grid grid-cols-3 gap-2 mt-5">
                            {whatsappDigits && (
                                <a
                                    href={`https://wa.me/${whatsappDigits}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="rounded-md bg-green-500 hover:bg-green-600 text-white text-xs font-medium py-3 flex flex-col items-center gap-1 transition-colors"
                                    data-testid="virtual-card-whatsapp"
                                >
                                    <MessageCircle className="w-4 h-4" />WhatsApp
                                </a>
                            )}
                            {card.phone && (
                                <a
                                    href={`tel:${card.phone}`}
                                    className="rounded-md bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium py-3 flex flex-col items-center gap-1 transition-colors"
                                    data-testid="virtual-card-call"
                                >
                                    <Phone className="w-4 h-4" />Llamar
                                </a>
                            )}
                            {card.email && (
                                <a
                                    href={`mailto:${card.email}`}
                                    className="rounded-md bg-slate-900 hover:bg-slate-700 text-white text-xs font-medium py-3 flex flex-col items-center gap-1 transition-colors"
                                    data-testid="virtual-card-email"
                                >
                                    <Mail className="w-4 h-4" />Email
                                </a>
                            )}
                        </div>

                        {/* Contact rows */}
                        <div className="mt-5 space-y-1.5 text-sm">
                            {card.phone && (
                                <a href={`tel:${card.phone}`} className="flex items-center gap-3 text-slate-700 hover:text-orange-600 px-3 py-2 rounded-md hover:bg-orange-50/40 transition-colors">
                                    <Phone className="w-4 h-4 text-slate-400" /><span className="truncate">{card.phone}</span>
                                </a>
                            )}
                            {card.email && (
                                <a href={`mailto:${card.email}`} className="flex items-center gap-3 text-slate-700 hover:text-orange-600 px-3 py-2 rounded-md hover:bg-orange-50/40 transition-colors break-all">
                                    <Mail className="w-4 h-4 text-slate-400 shrink-0" /><span className="truncate">{card.email}</span>
                                </a>
                            )}
                            {card.website && (
                                <a href={card.website.startsWith("http") ? card.website : `https://${card.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-slate-700 hover:text-orange-600 px-3 py-2 rounded-md hover:bg-orange-50/40 transition-colors">
                                    <Globe className="w-4 h-4 text-slate-400" /><span className="truncate">{card.website}</span>
                                </a>
                            )}
                        </div>

                        {/* Social icons */}
                        {(card.linkedin || card.instagram || card.twitter || card.facebook) && (
                            <div className="mt-4 flex items-center justify-center gap-3 flex-wrap" data-testid="virtual-card-socials">
                                {card.linkedin && (
                                    <a
                                    href={card.linkedin.startsWith("http") ? card.linkedin : `https://${card.linkedin}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label="LinkedIn"
                                    className="w-10 h-10 rounded-full bg-slate-100 hover:bg-blue-50 text-blue-700 flex items-center justify-center transition-colors"
                                    >
                                    <Linkedin className="w-4 h-4" />
                                    </a>
                                    )}
        {card.instagram && (
            <a
                href={card.instagram.startsWith("http") ? card.instagram : `https://${card.instagram}`}
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
                className="w-10 h-10 rounded-full bg-slate-100 hover:bg-pink-50 text-pink-600 flex items-center justify-center transition-colors"
            >
                <Instagram className="w-4 h-4" />
            </a>
        )}
        {card.twitter && (
            <a
                href={card.twitter.startsWith("http") ? card.twitter : `https://${card.twitter}`}
                target="_blank"
                rel="noreferrer"
                aria-label="X / Twitter"
                className="w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-900 flex items-center justify-center transition-colors"
            >
                <Twitter className="w-4 h-4" />
            </a>
        )}
        {card.facebook && (
            <a
                href={card.facebook.startsWith("http") ? card.facebook : `https://${card.facebook}`}
                target="_blank"
                rel="noreferrer"
                aria-label="Facebook"
                className="w-10 h-10 rounded-full bg-slate-100 hover:bg-blue-50 text-blue-700 flex items-center justify-center transition-colors"
            >
                <Facebook className="w-4 h-4" />
            </a>
        )}
    </div>
)}


                        {/* QR / share block removed from the public card per
                            product requirement — sharing options live inside
                            the authenticated CRM (/share-card). */}
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
}
